import { JsonFormErrorFloatingPane } from '../JsonFormErrorFloatingPane';
import { fakeRequired, uppercaseOptions, lightStyle } from '../ThinkalphaTheme';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { rankWith, uiTypeIs, schemaTypeIs, and } from '@jsonforms/core';
import type { ControlProps } from '@jsonforms/core';
import { withJsonFormsControlProps } from '@jsonforms/react';
import { cn } from '@thinkalpha/ui-core/utils';
import type { KeysWithoutReturnObjects } from 'i18next';
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Errors, Description, Label } from 'src/components/JsonForm/helpers';
import { useJsonFormExternalConfig } from 'src/components/JsonForm/JsonFormExternalConfigContext';
import {
    PopoverTrigger,
    Button,
    PopoverContent,
    Command,
    CommandInput,
    CommandEmpty,
    CommandList,
    CommandGroup,
    CommandItem,
    Popover,
} from 'src/components/ui';
import { useEvent } from 'src/hooks/useEvent';

interface Option {
    title: string;
    const: string;
}

export const ArrayControlComponent = ({
    path,
    data,
    handleChange,
    description,
    errors,
    label,
    required,
    visible,
    schema,
}: ControlProps) => {
    const { t } = useTranslation();
    const config = useJsonFormExternalConfig();

    const hasErrors = errors.length > 0;
    const dataArray = useMemo(() => (data ? (Array.isArray(data) ? data : [data]) : []), [data]);

    const items = useMemo(
        () =>
            schema.items as {
                enum?: string[];
                oneOf?: { const: string; title?: string }[];
                anyOf?: { const: string; title?: string }[];
            },
        [schema.items],
    );

    const options: Option[] = useMemo(() => {
        if (items?.enum) {
            return items.enum.map((item) => ({
                title: item,
                const: item,
            }));
        }

        return (items?.oneOf ?? items?.anyOf ?? []).map((item) => ({
            title: item.title || item.const,
            const: item.const,
        }));
    }, [items]);

    const selectedOptions: Option[] = useMemo(
        () => options.filter((o) => dataArray.includes(o.const)),
        [dataArray, options],
    );

    if (fakeRequired.includes(path)) {
        required = true; // make it look like required
    }

    const isLight = lightStyle.includes(path);

    const uppercase = uppercaseOptions.includes(path);

    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    const handleOnSelect = useEvent((value: Option) => {
        const newData = dataArray.includes(value.const)
            ? dataArray.filter((d) => d !== value.const)
            : [...dataArray, value.const];
        handleChange(path, newData);
    });

    return visible ? (
        <div>
            <div className="flex items-center gap-2">
                <Label label={label} required={required} />
                <Description description={description} />
            </div>

            <JsonFormErrorFloatingPane hasErrors={hasErrors} errors={<Errors errors={errors} />}>
                <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            variant={isLight ? 'light' : 'outline'}
                            size="2xs"
                            role="combobox"
                            className={cn(
                                'w-full justify-between overflow-hidden',
                                !data && 'text-gray-250',
                                uppercase && 'uppercase',
                                hasErrors &&
                                    (isLight
                                        ? 'bg-[#fbcdc1] dark:border-[#7a0808]'
                                        : 'bg-[#1D110E] dark:border-[#4C1919]'),
                            )}
                            disabled={false}
                        >
                            <span className="truncate">
                                {selectedOptions.map((o, i) => (
                                    <React.Fragment key={o.const}>
                                        {
                                            t(
                                                `${config.translationPrefix}::options::${o.title}` as KeysWithoutReturnObjects['json'],
                                                o.title,
                                            ) as unknown as string
                                        }
                                        {i < selectedOptions.length - 1 && <span>, </span>}
                                    </React.Fragment>
                                ))}
                            </span>
                            <FontAwesomeIcon icon={faChevronDown} className="ml-2 h-2.5 w-2.5 shrink-0 opacity-50" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0">
                        <Command>
                            <CommandInput placeholder="Search..." />
                            <CommandEmpty>No results found.</CommandEmpty>
                            <CommandList>
                                <CommandGroup>
                                    {options.map((o) => (
                                        <CommandItem value={o.const} key={o.const} onSelect={() => handleOnSelect(o)}>
                                            <FontAwesomeIcon
                                                icon={faCheck}
                                                className={cn(
                                                    'mr-2',
                                                    dataArray.includes(o.const) ? 'opacity-100' : 'opacity-0',
                                                )}
                                            />

                                            <span className={cn(uppercase && 'uppercase')}>
                                                {
                                                    t(
                                                        `${config.translationPrefix}::options::${o.title}` as KeysWithoutReturnObjects['json'],
                                                        o.title,
                                                    ) as unknown as string
                                                }
                                            </span>
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>
            </JsonFormErrorFloatingPane>
        </div>
    ) : null;
};

export const ArrayControlTester = rankWith(15, and(uiTypeIs('Control'), schemaTypeIs('array')));

export const ArrayControl = withJsonFormsControlProps(ArrayControlComponent);
